import {
  DROP_LOCATIONS as SHARED_DROP_LOCATIONS,
  DROP_LOCATION_LABEL,
  FAIL_REASONS as SHARED_FAIL_REASONS,
  FAIL_REASON_LABEL,
} from '@delivery/shared';
import { AlarmClock, Ban, DoorClosed, Lock, MapPin, Package, PhoneOff, Plus, type LucideIcon } from 'lucide-react';

/** 图标只在司机端有意义,留在这边 */
const FAIL_ICON: Record<(typeof SHARED_FAIL_REASONS)[number], LucideIcon> = {
  NO_ONE_HOME: DoorClosed,
  PHONE_UNREACHABLE: PhoneOff,
  WRONG_ADDRESS: MapPin,
  REFUSED: Ban,
  NO_ACCESS: Lock,
  DAMAGED: Package,
  RESCHEDULED: AlarmClock,
  OTHER: Plus,
};

/** 与后端 /driver/tasks 的响应对应 */

export interface ProofRequirement {
  required: boolean;
  mode: 'PER_BATCH' | 'PER_ORDER';
  minPhotos: number;
  maxPhotos: number;
  hint?: string;
}

export interface Task {
  id: string;
  orderNo: string;
  /**
   * 智能推荐路线里排第几(1 起)。null = 不在这次规划里。
   *
   * ★ 可选:离线缓存里可能是老版本服务端的响应。
   */
  routeSeq?: number | null;
  /** 预计几点到(含每一单的停留时间) */
  routeEtaAt?: string | null;
  status: OrderStatus;
  subStatus: string | null;
  version: number;

  /** 后端算好的「当前该去的位置」—— 前端不用自己判断取货还是送货 */
  target: 'PICKUP' | 'DELIVERY';
  lat: number | null;
  lng: number | null;

  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  /** 取货之后地址被改过的那一刻。null = 没改过 */
  addressChangedAt?: string | null;
  receiverNote: string | null;
  /** 联系人身上「长期成立」的派送备注(门禁密码、走侧门)—— 和上面那条并列显示 */
  /** 自己加的备注 —— 显示在订单备注的上一行 */
  driverNote?: string | null;
  /**
   * 收货地坐标 —— **始终有**,不像 `lat/lng` 那样跟着当前阶段变。
   * 取货阶段也要能看见「拿完往哪送」。
   */
  receiverLat: number | null;
  receiverLng: number | null;

  /**
   * 发货方。
   *
   * ★ 联系人和电话是这一轮加的 —— 原来只有名称和地址,
   *   而司机到了店里找不到人时,唯一的办法是打给调度、
   *   让调度再打给商家。
   */
  pickup: {
    id: string | null;
    name: string;
    address: string;
    contactName: string | null;
    contactPhone: string | null;
    lat: number | null;
    lng: number | null;
  };
  /** @deprecated 用 `pickup` —— 这两个留着是为了不一次改动所有调用点 */
  pickupPoint: { id: string; name: string; address: string } | null;
  pickupAddress: string;

  zone: { code: string; name: string } | null;
  packageCount: number;
  weightGram: number | null;
  codAmount: string;
  /** 要代收但金额待定 —— 界面写「联系商家确认」,不写 0 */
  codPending?: boolean;
  /**
   * 冻品、易碎…… 名字是**后端翻译好**再下发的。
   *
   * ★ 不下发只有 code 的话,进了地下车库离线打开这一单,
   *   司机看到的是「FROZEN」四个字母 —— 字典没有缓存在本地。
   */
  specialItems?: Array<{ code: string; label: string; icon: string | null }>;
  payout: string;
  currency: string;

  expectedDeliveryAt: string | null;
  acceptExpiresAt: string | null;

  proof: { pickup: ProofRequirement; delivery: ProofRequirement };
  customerId: string | null;

  failedAttempts: number;
  remark: string | null;
  /** 建单时间 —— 用来认出「这单是今天之前的,还没走完」 */
  createdAt?: string | null;
  /** 派到这个司机手上的时间 / 他接下来的时间。见 Today.tsx 里 stale 那段 */
  assignedAt?: string | null;
  acceptedAt?: string | null;
}

export type OrderStatus =
  | 'PENDING'
  | 'ASSIGNED'
  | 'ACCEPTED'
  | 'PICKED_UP'
  | 'IN_TRANSIT'
  | 'DELIVERED'
  | 'FAILED'
  | 'RETURNED'
  | 'CANCELLED';

export interface TasksResponse {
  tasks: Task[];
  counts: {
    pendingAccept: number;
    toPickup: number;
    toDeliver: number;
    failed: number;
    doneToday: number;
  };
  serverTime: string;
  /**
   * 因为「非当日达」被服务端藏掉的单数。
   *
   * ★ 可选:离线缓存里可能是**老版本服务端**的响应,没有这个字段。
   *   标成必填的话 TS 不会报错(JSON 不校验),而 `undefined` 会
   *   一路流到界面上显示成「还有 undefined 单」。
   */
  futureHidden?: number;
  /**
   * 推荐路线的概况。没规划过是 null。
   *
   * ★ `unplanned` 是关键:规划之后新派进来的单不在那个顺序里 ——
   *   不提示的话司机按列表走到最后才发现还有三单,而那几单可能在
   *   他已经走过的那一头。
   */
  route?: { plannedAt: string; planned: number; unplanned: number } | null;
}

export interface PickupGroup {
  pickupPointId: string | null;
  name: string;
  shortName: string | null;
  type: string;
  address: string;
  lat: number | null;
  lng: number | null;
  contactName: string | null;
  contactPhone: string | null;
  cutoffTime: string | null;
  /** 新司机第一次去最需要的三样 */
  pickupInstruction: string | null;
  parkingNote: string | null;
  photoKey: string | null;
  geofence: { required: boolean; radiusM: number };
  distanceM: number | null;
  orderCount: number;
  earliestDeadline: string | null;
  /** 组内按商家分 —— 拍照要求按商家算 */
  customerGroups: CustomerGroup[];
}

export interface CustomerGroup {
  customerId: string;
  customerName: string;
  proof: ProofRequirement;
  orders: Array<{
    id: string;
    orderNo: string;
    receiverName: string;
    packageCount: number;
    weightGram: number | null;
    version: number;
  }>;
}

export interface Me {
  driver: {
    publicId: string;
    driverNo: string;
    name: string;
    phone: string;
    driverType: string;
    status: string;
    dutyStatus: 'ONLINE' | 'OFFLINE' | 'BUSY';
    vehicleType: string | null;
    plateNo: string | null;
    creditScore: number;
    currentOrderCount: number;
    docsVerifiedAt: string | null;
  };
  /** 前端据此显隐入口:不能抢单就不显示「可领取」 */
  policy: {
    /** ★ 有效值:租户开了抢单池 **且** 这个账号允许抢单(见 driver.service) */
    canGrab: boolean;
    /** 不能抢单时是哪一边关的:功能开关 / 这个账号的策略。能抢时是 null */
    grabDisabledBy: 'FEATURE' | 'POLICY' | null;
    /** 派单免确认:开了之后没有「待接单」这一步 */
    assignWithoutAccept: boolean;
    /** 「配送中」这个状态是否启用 */
    canStartTransit: boolean;
    /** 只显示「送达」—— 点一下直接进送达面板,不先选「送达/未送达」 */
    deliverOnly: boolean;
    /** 代收、特殊物品这些确认项打开就是勾上的 */
    confirmPrechecked: boolean;
    /** 租户开了「特殊物品」没有 —— 决定列表里那一栏在不在 */
    specialItems: boolean;
    /** 电话旁边显不显示 WhatsApp 按钮 */
    whatsapp: boolean;
    canBeAssigned: boolean;
    canReject: boolean;
    maxConcurrentOrders: number;
    requireShift: boolean;
    requireDocsVerified: boolean;
  };
  today: Record<string, number>;
  /** ★ timezone 是租户时区。班次的「今天」按它算,不是手机本地时区 */
  tenant: {
    code: string;
    timezone: string;
    /** 底图配置(服务端已把 {key} 替换好)。null 表示后台没配,前端回落到构建期变量 */
    tiles?: { tileUrl: string | null; attribution: string | null; keyMissing: boolean } | null;
  };
  /** VAPID 公钥。为空表示服务端没配推送 —— 这时候不要去订阅 */
  push?: { publicKey: string | null };
}

/**
 * 投递失败的预置原因 —— 司机在门口单手操作,不能让他打字。
 *
 * ★ 文案来自 shared,这里只补图标。
 *   司机选「客户拒收」,管理端时间轴必须显示同样四个字 ——
 *   两边各写一份,迟早一边改了另一边没跟上
 */
export const FAIL_REASONS = SHARED_FAIL_REASONS.map((code) => ({
  code,
  icon: FAIL_ICON[code],
  label: FAIL_REASON_LABEL[code],
}));

export const DROP_LOCATIONS = SHARED_DROP_LOCATIONS.map((code) => ({
  code,
  label: DROP_LOCATION_LABEL[code],
}));

export const STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: '待领取',
  ASSIGNED: '待接单',
  ACCEPTED: '待取货',
  PICKED_UP: '配送中',
  IN_TRANSIT: '配送中',
  DELIVERED: '已送达',
  FAILED: '未送达',
  RETURNED: '已退回',
  CANCELLED: '已取消',
};
